import { Component, Input, OnDestroy, OnInit, EventEmitter, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatChipInputEvent } from '@angular/material/chips';
import { FileItem } from '@models/file-item.model';
import { Product } from '@models/product.model';
import { PurchaseOrderDto } from '@app/models/purchase-order.model.dto';
import { Shipment, ShipmentFile, ShipmentLineItem, Reference } from '@models/shipment.model';
import { NgbModal, NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FileService } from '@services/file.service';
import { ShipmentService } from '@services/shipment.service';
import { Toaster } from '@services/toaster.service';
import { CustomPopupComponent } from '@theme/components';
import { LocalStorage } from 'ngx-webstorage';
import { BehaviorSubject, forkJoin, Observable, of, Subject } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import CONFIG from '@app/app.config';
import { BusinessValidator, DateIsSameOrAfter } from '@app/shared';
import { CompanyService } from '@app/services';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { environment } from '@env/environment';
import {COMMA, ENTER} from '@angular/cdk/keycodes';

@Component({
  selector: 'ngx-shipment-tracking-form-modal',
  templateUrl: './shipment-tracking-form-modal.component.html',
  styleUrls: ['./shipment-tracking-form-modal.component.scss'],
})
export class ShipmentTrackingFormModalComponent implements OnInit, OnDestroy {
  isRCDSS = environment.name === 'rcdss';
  shipmentForm: FormGroup;
  shipmentLineItemsForm: FormGroup;
  availableLineItems: any[] = [];
  selectedLineItems: ShipmentLineItem[] = [];
  lineItemsFiltered$: Observable<any>;
  lineItems$ = new BehaviorSubject('');

  @Input() purchaseOrder: PurchaseOrderDto;
  @Input() products: Product[];
  @Input() shipment: Shipment;
  @Input() shipments: Shipment[];
  @Input() showFormSwitch = true;
  @LocalStorage() organizationId;
  @Input() companyType;

  @ViewChild("po_number") poSelect;
  isPoCreation = false;
  poNumber: string;
  poCreatedEventEmitter = new EventEmitter<PurchaseOrderDto>();
  enablePoCreationBinded;

  currentShipment: Shipment;
  disableControls: Boolean = true;

  filesObject: File[] = [];
  filesUploaded: ShipmentFile[] = [];
  purchaseOrders$: Observable<PurchaseOrderDto[]>;
  selectedPO;
  showPOField = false;
  carriers: any[];
  selectedFormView: string = 'enhancedTracking';
  selectedCarrier;

  showEnhanceTrackingForm: boolean = true;

  loading: boolean = false;

  references: string[] = [];
  addOnBlur = false;
  readonly separatorKeysCodes = [ENTER, COMMA] as const;

  private destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private modalService: NgbModal,
    private ngbActiveModal: NgbActiveModal,
    private shipmentService: ShipmentService,
    private companyService: CompanyService,
    private fb: FormBuilder,
    private $toaster: Toaster,
    private matDialog: MatDialog,
    private fileService: FileService,
  ) {
  }

  ngOnInit() {
    if (this.purchaseOrder) {
      this.showEnhanceTrackingForm = this.purchaseOrder.is_simple;
      this.showFormSwitch = !this.purchaseOrder.is_simple;
      if (!this.purchaseOrder.is_simple) {
        if (this.purchaseOrder.line_items) {
          this.checkAvailableLineItems();
        }
      }
      if (this.showEnhanceTrackingForm) {
        this.getShipmentsCarriers();
      }
    }

    this.poCreatedEventEmitter.subscribe(po => {
      this.getPurchaseOrders(po.id);
      this.selectPO(po);
      this.isPoCreation = false;
      this.poNumber = null;
    });

    this.enablePoCreationBinded = this.enablePoCreation.bind(this);

    this.initForms();

    if (!this.purchaseOrder) {
      this.showPOField = true;
      this.shipmentForm.disable();
      this.shipmentLineItemsForm.disable();
      this.getPurchaseOrders();
    }

    if (this.shipment) {
      this.loadShipmentInForm(this.shipment);
    }

  }

  getPurchaseOrders(selectedPoId = null) {
    if (selectedPoId) {
      this.loading = true;
    }
    this.purchaseOrders$ = this.companyService.fetchPurchaseOrders(this.companyType)
      .pipe(map((data: PurchaseOrderDto[]) => {
        return data.map((item: PurchaseOrderDto) => {
          if (item.id === selectedPoId) {
            // item.selected = true;
            this.selectedPO = item;
            this.loading = false;
          }
          item.po_number_supplier_name = `${item.customer_po_number} (${item.supplier_name})`;
          return item;
        });
      }));
  }

  initForms() {

    let formConfig: any = {
      description: [],
      link: ['', [Validators.required, Validators.pattern(CONFIG.URL_REGEX)]],
      tracking_code: [],
      shipped_at: ['', [Validators.required]],
      arrives_at: [],
    };

    if (this.showEnhanceTrackingForm) {
      formConfig = {
        enhanced_tracking_carrier_id: ['',
          BusinessValidator.optionalIfExists('enhanced_tracking_identifier')],
        enhanced_tracking_identifier_type: ['',
          BusinessValidator.optionalIfExists('enhanced_tracking_identifier')],
        enhanced_tracking_identifier: [null],
      };
    }

    this.shipmentForm = this.fb.group(formConfig);

    if (!this.showEnhanceTrackingForm) {
      this.shipmentForm.controls['arrives_at']
        .setValidators(DateIsSameOrAfter(this.shipmentForm.controls['shipped_at']));

    }

    this.shipmentLineItemsForm = this.fb.group({
      line_item_id: ['', [Validators.required]],
      quantity_shipped: ['', [Validators.required, Validators.min(1)]],
    });
  }

  selectCarrier($event) {
    this.selectedCarrier = this.carriers.find(c => c.id === $event.value);
  }

  getShipmentsCarriers() {
    this.shipmentService.shipmentCarriers(this.organizationId, this.purchaseOrder.id)
      .subscribe(data => {
        this.carriers = data;
      });
  }

  checkAvailableLineItems() {
    if (!this.purchaseOrder.line_items) {
      return;
    }
    this.availableLineItems = this.purchaseOrder.line_items.map((item) => {
      item.product_name = item.product_name || this.displayProductName(item.product_id);
      return item;
    });

    this.lineItemsFiltered$ = this.lineItems$.pipe(
      switchMap((term: string) => {
        const regex = new RegExp(term, 'i');
        const availableItems = this.availableLineItems
          .filter(
            (item) => !this.selectedLineItems
              .find((selectedItem) => item.id === selectedItem.line_item_id),
          ).filter(item => regex.test(item.product_name));
        return of(availableItems);
      }),
    );
  }

  loadShipmentInForm(shipment) {
    this.shipmentForm.reset();
    this.shipmentForm.patchValue(shipment);
    if (shipment.line_items) {

      shipment.line_items.forEach(li => {
        const shipmentLineItem = li;
        const PoLineItem = this.purchaseOrder.line_items.find(item => item.id === shipmentLineItem.line_item_id);
        if (PoLineItem) {
          shipmentLineItem.name = PoLineItem.product_name || this.displayProductName(PoLineItem.product_id);
        }

        this.selectedLineItems.push(shipmentLineItem);
        this.shipmentLineItemsForm.reset();
      });
      this.lineItems$.next('');
    }

    if (shipment.file) {
      this.filesUploaded = [shipment.file];
    }
  }

  searchShipment($event) {
    const controls = [
      'description',
      'link',
      'shipped_at',
      'arrives_at',
    ];
    return this.shipmentService.getShipmentByTrackingCode(this.organizationId, $event.target.value)
      .subscribe(async (shipment) => {
          if (shipment) {
            if (this.shipments && this.shipments.find(s => s.id === shipment.id)) {
              const modal = this.matDialog.open(CustomPopupComponent, {
                disableClose: true,
              });
              modal.componentInstance.description = 'There is a shipment with the same tracking code, would you like to edit it?';
              await modal.afterClosed().toPromise().then(result => {
                if (result === 'success') {
                  this.shipment = shipment;
                  this.loadShipmentInForm(shipment);
                } else {
                  this.closeModal();
                }
              });
              return;
            }

            this.currentShipment = shipment;
            this.shipmentForm.patchValue(shipment);
            controls.forEach(controlName => this.shipmentForm.get(controlName).disable());
          }
        },
        () => {
          if (!this.shipment) {
            controls.forEach(controlName => this.shipmentForm.get(controlName).reset());
            this.shipmentForm.enable();
          }
        });
  }

  formSubmit() {
    return this.shipment && this.shipment.id ? this.updateShipment() : this.saveShipment();
  }

  saveShipment() {
    if (!this.shipmentForm.valid) {
      return;
    }

    this.loading = true;
    const shipment = new Shipment(this.shipmentForm.getRawValue());
    let request: Observable<any>;
    if (!this.currentShipment) {
      shipment.line_items = this.selectedLineItems;
      request = this.shipmentService.addPurchaseOrderShipment(this.organizationId, this.purchaseOrder.id, shipment);
    } else {
      request = this.shipmentService.addItemsToShipment(
        this.organizationId,
        this.purchaseOrder.id,
        this.currentShipment.id,
        this.selectedLineItems,
      );
    }

    request.pipe(switchMap((result) => {
        if (this.filesObject && this.filesObject.length) {
          return this.uploadFiles(result.id).pipe(map(() => of(result)));
        }
        return of(result);
      }),
    ).subscribe(async (result) => {
      for (let reference of this.references) {
        let res = await this.shipmentService.addReference(result.organization_id, result.id, reference);
        res.subscribe(data => {
          //
        }, error => {
          console.error(error);
        });
      }
      this.$toaster.show('success', 'Success', 'Shipment tracking has been added');
      this.filesObject = [];
      this.loading = false;
      this.ngbActiveModal.close(result);
    }, (e) => {
      const errMsg = !!e.error && !!e.error.detail ? e.error.detail : e.message;
      this.$toaster.show('error', 'Error adding the shipment tracking', errMsg);
      this.loading = false;
    });
  }

  updateShipment() {
    if (this.shipmentForm.valid && !!this.selectedLineItems.length) {
      const shipment = {
        ...this.shipment,
        ...this.shipmentForm.getRawValue(),
      };
      let request: Observable<any>;
      request = this.shipmentService.updatePurchaseOrderShipment(
        this.purchaseOrder.supplier_id,
        this.purchaseOrder.id,
        this.shipment.id,
        shipment,
      );

      if (this.filesObject && this.filesObject.length) {

        if (shipment && shipment.id && shipment.file) {
          request = request.pipe(switchMap((result) => {
            return this.deleteFile(shipment.file.file).pipe(map(() => result));
          }));
        }

        request = request.pipe(switchMap((result) => {
            return this.uploadFiles(result.id).pipe(map(() => of(result)));
          }),
        );
      }
      request.subscribe((result) => {
        this.$toaster.show('success', 'Success', 'Shipment tracking has been updated');
        this.filesObject = [];
        this.ngbActiveModal.close(result);
      }, (e) => {
        const errMsg = !!e.error && !!e.error.detail ? e.error.detail : e.message;
        this.$toaster.show('error', 'Error updating the shipment tracking', errMsg);
      });
    }
  }

  removeShipmentLineItem(lineItem, index) {

    if (this.selectedLineItems.length === 1) {
      this.$toaster.show('warning', 'Shipment Tracking', 'The shipment must keep at least one line item');
      return;
    }

    if (this.shipment && this.shipment.id) {
      this.shipmentService.deleteLineItemShipment(this.organizationId, this.purchaseOrder.id, this.shipment.id, lineItem.id)
        .subscribe((r) => {
          console.log(r);
        });
      return;
    }

    this.selectedLineItems.splice(index, 1);
    this.lineItems$.next('');
    if (!this.selectedLineItems.length) {
      this.shipmentLineItemsForm.get('line_item_id').setValidators([Validators.required]);
      this.shipmentLineItemsForm.get('quantity_shipped').setValidators([Validators.required, Validators.min(1)]);
    }
  }

  // TODO remove this function when backend add the product name to line items

  addShipmentLineItem() {
    if (
      this.shipmentLineItemsForm.valid &&
      (
        !!this.shipmentLineItemsForm.get('line_item_id').value &&
        !!this.shipmentLineItemsForm.get('quantity_shipped').value
      )
    ) {
      const lineItemValue = this.shipmentLineItemsForm.getRawValue();
      const lineItem = this.availableLineItems.find(i => i.id === lineItemValue.line_item_id);

      if (((lineItemValue.quantity_shipped + lineItem.shipped_to_date) > lineItem.quantity_complete) &&
        this.purchaseOrder.finished_goods_reports_period !== 'No report needed') {
        const msj = `The total shipped cannot be greater than the ${lineItem.quantity_complete} units completed`;
        this.$toaster.show('error', 'Error adding the line item.', msj);
        return;
      }

      this.selectedLineItems.push({
        ...lineItemValue,
        name: lineItem.product_name,
      });

      this.shipmentLineItemsForm.reset();
      this.lineItems$.next('');

      this.shipmentLineItemsForm.get('line_item_id').setValidators([]);
      this.shipmentLineItemsForm.get('quantity_shipped').setValidators([Validators.min(1)]);


      if (this.shipment && this.shipment.id) {

        const line_items = [lineItemValue];
        this.shipmentService.addItemsToShipment(this.organizationId, this.purchaseOrder.id, this.shipment.id, line_items)
          .subscribe((r) => {
          });
      }

    }
  }

  closeModal() {
    /*this.shipmentForm.reset();
    this.shipmentLineItemsForm.reset();
    this.shipmentLineItemsForm.get('line_item_id').setValidators([Validators.required]);
    this.shipmentLineItemsForm.get('quantity_shipped').setValidators([Validators.required, Validators.min(1)]);*/
    this.ngbActiveModal.dismiss();
  }

  displayProductName(productId: string): string {
    if (this.products && this.products.length) {
      const prod = this.products.find(p => p.id === productId);
      return !!prod ? prod.name.charAt(0).toUpperCase() + prod.name.slice(1) : productId;
    }
    return productId;
  }

  addFile(event: any) {
    const fileList: FileList = event.srcElement.files;

    if (fileList.length === 0) {
      return;
    }

    for (let i = 0; i < fileList.length; i++) {
      const file = fileList.item(i);
      if (file.size > CONFIG.FILE_LIMIT) {
        this.$toaster.show('warning', 'File size', 'Maximum file size for uploads is 10 mb');
        continue;
      }
      this.filesObject = [file];
      this.filesUploaded = [];
    }

  }

  removeFile(file, $index) {
    if (file.file && file.file.file_id) {
      this.filesUploaded.splice($index, 1);
      return;
    }
    this.filesObject.splice($index, 1);
  }

  uploadFiles(shipmentId) {
    const filesUploadRequest = this.filesObject.map((file) => {
      return this.shipmentService.addShipmentFile(this.organizationId, this.purchaseOrder.id, shipmentId, file);
    });
    return forkJoin(filesUploadRequest);
  }

  deleteFile(file) {
    return this.shipmentService.deleteShipmentFile(
      this.organizationId,
      this.purchaseOrder.id,
      this.shipment.id,
      file.file_id,
    );
  }

  getFile(file: FileItem) {
    this.shipmentService.getShipmentFile(this.organizationId, this.purchaseOrder.id, this.shipment.id, file.file_id)
      .subscribe((blobFile) => {
        file.file_name.endsWith('.pdf') ?
          this.fileService.viewPdf(blobFile, file.file_name) :
          this.fileService.downloadBlobFile(blobFile, file.file_name);
      });
  }

  selectPO($event) {
    this.purchaseOrder = $event;
    const counterpartCompanyID = this.companyType === 'customer' ?
      this.purchaseOrder.supplier_id :
      this.purchaseOrder.customer_id;

    forkJoin([
      this.shipmentService.getPurchaseOrderShipments(this.purchaseOrder.supplier_id, this.purchaseOrder.id),
      this.companyService.getProductsByCompanyId(counterpartCompanyID),
    ]).subscribe(([shipments, products]) => {
        this.shipments = shipments;
        this.products = products;
        this.checkAvailableLineItems();
        this.shipmentForm.enable();
        this.shipmentLineItemsForm.enable();
        this.getShipmentsCarriers();
      },
      (error) => {
        console.error(error);
        const errMsg = error.error && error.error.detail ? error.error.detail : error.message;
        this.$toaster.show('error', this.isRCDSS ? 'Contract' : 'Purchase Order', errMsg);
      },
    );
  }

  changeFormView($event: MatButtonToggleChange) {
    this.showEnhanceTrackingForm = $event.value === 'enhancedTracking';
    if (this.showEnhanceTrackingForm && this.purchaseOrder) {
      this.getShipmentsCarriers();
    }
    this.initForms();
  }

  addReference(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if (this.references.includes(value)) {
      this.$toaster.show('error', 'Duplicated reference', 'Please use unique references');
    }
    this.references.push(value);
    event.input.value = '';
  }

  removeReference(reference: string): void {
    let i = this.references.indexOf(reference);
    if (i !== -1) {
      this.references.splice(i, 1);
    }
  }

  enablePoCreation(po_number) {
    this.isPoCreation = true;
    this.poNumber = po_number;
  }

  comparePOs(poA: any, poB: any): boolean {
    return poA.id === poB.id;
  }

  ngOnDestroy() {
    this.lineItems$.unsubscribe();
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
